/* eslint-disable indent */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { IoIosArrowDown } from 'react-icons/io';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Stack,
  Text,
  Icon,
  AccordionPanel,
  Select,
  Spinner,
  Textarea,
  Flex,
  Button,
  Image,
  Input,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  updateSymptom,
  useAppDispatch,
  useLazyListServicesQuery,
} from '@spartalabs/pdc-core';
import { useCustomToast } from 'hooks';
import { generateId } from 'utils/functions';
import * as z from 'zod';
import trashSvg from '../../../../../assets/svg/trash.svg';

const SelectLoading = () => {
  return <Spinner size="sm" color="grey.600" />;
};

const ServiceFormData = z.object({
  details: z.array(z.string()).optional(),
  observation: z.string().optional(),
});

type ServiceFormSchema = z.infer<typeof ServiceFormData>;

interface ServiceProps {
  service?: ScheduleSymptom;
  hasDivider?: boolean;
  handleAddService: (service: ScheduleSymptom) => void;
  handleAddImage: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => void;
  images: ScheduleFile[];
  handleDeleteImage: (name: string) => void;
}

const Service = forwardRef<ScheduleSymptom, ServiceProps>(
  (
    {
      service,
      hasDivider,
      handleAddService,
      images,
      handleAddImage,
      handleDeleteImage,
    },
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const { handleToast } = useCustomToast();
    const [currentId, setCurrentId] = useState<string>(generateId());

    const [fetchServices, { isLoading, isFetching }] =
      useLazyListServicesQuery();

    const { register, handleSubmit, setValue, getValues, watch } =
      useForm<ServiceFormSchema>({
        resolver: zodResolver(ServiceFormData),
      });

    const [currentService, setCurrentService] = useState(service);
    useEffect(() => {
      if (currentService?.id !== service?.id) {
        setCurrentService(service);
        resetForm();
        setValue('observation', service?.observation);
        setFirstLevelServices(service?.firstLevelOptions);
        setSecondLevelServices(
          service?.details?.[0] ? service?.secondLevelOptions : [],
        );
        setThirdLevelServices(
          service?.details?.[1] ? service?.thirdLevelOptions : [],
        );
        setFourthLevelServices(
          service?.details?.[2] ? service?.fourthLevelOptions : [],
        );
      }
    }, [service]);

    const [firstLevelServices, setFirstLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.firstLevelOptions || undefined);
    const [secondLevelServices, setSecondLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.secondLevelOptions || undefined);
    const [thirdLevelServices, setThirdLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.thirdLevelOptions || undefined);
    const [fourthLevelServices, setFourthLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.fourthLevelOptions || undefined);

    const [firstId, setFirstId] = useState<ScheduleSymptomDetail | undefined>();
    const [secondId, setSecondId] = useState<
      ScheduleSymptomDetail | undefined
    >();
    const [thirdId, setThirdId] = useState<ScheduleSymptomDetail | undefined>();
    const [fourthId, setFourthId] = useState<
      ScheduleSymptomDetail | undefined
    >();

    const filtredImages = useMemo(() => {
      return images.filter(
        image => image.id === currentId || image.id === service?.id,
      );
    }, [currentId, service, images]);

    useImperativeHandle(
      ref,
      () => {
        const data = getValues();
        const details = data.details?.map(detail => {
          if (detail && detail !== '') return JSON.parse(detail);
        });
        const observation = data.observation;

        const notAddedService = { details, observation, id: currentId };

        return notAddedService;
      },
      [
        watch('details.0'),
        watch('details.1'),
        watch('details.2'),
        watch('details.3'),
        watch('observation'),
      ],
    );

    useEffect(() => {
      if (currentService) {
        if (currentService?.details?.[0]) {
          setValue('details.0', JSON.stringify(currentService.details[0]));
        }

        if (currentService?.details?.[1]) {
          setValue('details.1', JSON.stringify(currentService.details[1]));
        }

        if (currentService?.details?.[2]) {
          setValue('details.2', JSON.stringify(currentService.details[2]));
        }

        if (currentService?.details?.[3]) {
          setValue('details.3', JSON.stringify(currentService.details[3]));
        }
      } else {
        fetchFirstLevel();
      }
    }, []);

    useEffect(() => {
      if (currentService) {
        if (currentService?.details?.[0]) {
          setValue('details.0', JSON.stringify(currentService.details[0]));
        }

        if (currentService?.details?.[1]) {
          setValue('details.1', JSON.stringify(currentService.details[1]));
        }

        if (currentService?.details?.[2]) {
          setValue('details.2', JSON.stringify(currentService.details[2]));
        }

        if (currentService?.details?.[3]) {
          setValue('details.3', JSON.stringify(currentService.details[3]));
        }
      }
    }, [currentService]);

    useEffect(() => {
      if (service && currentService?.id === service.id) {
        dispatch(
          updateSymptom({
            id: service.id,
            details: currentService?.details?.filter(
              detail => detail !== undefined,
            ),
            observation: watch('observation'),
            firstLevelOptions: firstLevelServices,
            secondLevelOptions: secondLevelServices,
            thirdLevelOptions: thirdLevelServices,
            fourthLevelOptions: fourthLevelServices,
          }),
        );
      }
    }, [
      watch('details.0'),
      watch('details.1'),
      watch('details.2'),
      watch('details.3'),
      watch('observation'),
    ]);

    const resetForm = () => {
      setFirstId(undefined);
      setSecondId(undefined);
      setThirdId(undefined);
      setFourthId(undefined);
      setSecondLevelServices([]);
      setThirdLevelServices([]);
      setFourthLevelServices([]);
      setValue('observation', '');
      setValue('details.0', '');
      setValue('details.1', '');
      setValue('details.2', '');
      setValue('details.3', '');
      setCurrentId(generateId());
    };

    const fetchFirstLevel = useCallback(async () => {
      try {
        const firstLevelServices = await fetchServices({
          firstLevel: ' ',
        }).unwrap();

        setFirstLevelServices(firstLevelServices);
      } catch (error) {
        handleToast({
          step: 4,
          action: 'buscar serviços',
        });
      }
    }, []);

    const fetchSecondSelect = useCallback(
      async (
        services: ScheduleSymptomDetail[],
        currentServiceAux?: ScheduleSymptom,
      ) => {
        const service = services[0];
        setValue('details.0', JSON.stringify(service));

        try {
          const secondLevelServices = await fetchServices({
            firstLevel: service?.id,
          }).unwrap();

          setFirstId(service);
          setValue('details.0', JSON.stringify(service));

          setSecondLevelServices(secondLevelServices);
          if (currentServiceAux?.details?.[1]) {
            setValue('details.1', JSON.stringify(currentServiceAux.details[1]));
          }
        } catch (error) {
          handleToast({
            step: 4,
            action: 'buscar serviços',
          });
        }
      },
      [firstLevelServices, currentService],
    );

    const fetchThirdSelect = useCallback(
      async (
        services: ScheduleSymptomDetail[],
        currentServiceAux?: ScheduleSymptom,
      ) => {
        const service = services[1];
        try {
          const thirdLevelServices = await fetchServices({
            firstLevel: services[0].id,
            secondLevel: service.id,
          }).unwrap();

          setSecondId(service);

          setThirdLevelServices(thirdLevelServices);
          if (currentServiceAux?.details?.[2]) {
            setValue('details.2', JSON.stringify(currentServiceAux.details[2]));
          }
        } catch (error) {
          handleToast({
            step: 4,
            action: 'buscar serviços',
          });
        }
      },
      [secondLevelServices, currentService],
    );

    const fetchFourthSelect = useCallback(
      async (
        services: ScheduleSymptomDetail[],
        currentServiceAux?: ScheduleSymptom,
      ) => {
        const service = services[2];

        try {
          const fourthLevelServices = await fetchServices({
            firstLevel: services[0]?.id,
            secondLevel: services[1]?.id,
            thirdLevel: service?.id,
          }).unwrap();

          setThirdId(service);

          setFourthLevelServices(fourthLevelServices);
          if (currentServiceAux?.details?.[3]) {
            setValue('details.3', JSON.stringify(currentServiceAux.details[3]));
          }
        } catch (error) {
          handleToast({
            step: 4,
            action: 'buscar serviços',
          });
        }
      },
      [thirdLevelServices, currentService],
    );

    const onSubmit = (data: ServiceFormSchema) => {
      const details = data.details?.map(detail => {
        if (detail && detail !== '') return JSON.parse(detail);
      });
      const observation = data.observation;

      handleAddService({
        details,
        observation,
        id: currentId,
        firstLevelOptions: firstLevelServices,
        secondLevelOptions: secondLevelServices,
        thirdLevelOptions: thirdLevelServices,
        fourthLevelOptions: fourthLevelServices,
      });

      resetForm();
    };

    return (
      <Accordion defaultIndex={-1} allowToggle gap={0} w="100%">
        <AccordionItem
          isDisabled={
            service && (isLoading || isFetching) && !secondLevelServices
          }
          py="4px"
          borderTop="transparent"
          borderBottomColor={hasDivider ? undefined : 'transparent'}
        >
          {({ isExpanded }) => (
            <>
              <AccordionButton _hover={{ bg: 'transparent' }}>
                <Box as="span" flex="1" textAlign="left" color="black">
                  <Text color="text.100">
                    {currentService
                      ? currentService?.details?.[0]?.name ||
                        'Serviço não definido'
                      : 'Selecione o serviço'}
                  </Text>
                </Box>
                {service &&
                (isLoading || isFetching) &&
                !secondLevelServices ? (
                  <SelectLoading />
                ) : currentService ? (
                  <Icon
                    as={IoIosArrowDown}
                    color="text.100"
                    transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                  />
                ) : (
                  <Icon
                    as={isExpanded ? MinusIcon : AddIcon}
                    color="text.100"
                    transition="all .5s ease"
                    h="12px"
                    w="12px"
                  />
                )}
              </AccordionButton>
              <AccordionPanel display="flex" flexDir="column" gap="20px">
                <Select
                  size="lg"
                  bg="transparent"
                  border="1px solid"
                  borderColor="grey.600"
                  color="text.100"
                  fontSize="16px"
                  fontWeight="500"
                  placeholder="Selecionar serviço"
                  {...register('details.0', {
                    onChange: () => {
                      setFirstId(undefined);
                      setSecondId(undefined);
                      setThirdId(undefined);
                      setFourthId(undefined);
                      setSecondLevelServices(undefined);
                      setThirdLevelServices(undefined);
                      setFourthLevelServices(undefined);
                      setValue('details.1', '');
                      setValue('details.2', '');
                      setValue('details.3', '');
                      const services = getValues('details');
                      const parsedServices = services?.map(service => {
                        if (service && service !== '')
                          return JSON.parse(service);
                      });
                      if (service) {
                        setCurrentService(prev => {
                          const newCurrentService = {
                            ...prev,
                            details: parsedServices
                              ? [parsedServices[0]]
                              : undefined,
                          };

                          fetchSecondSelect(
                            newCurrentService.details as ScheduleSymptomDetail[],
                            newCurrentService,
                          );

                          return newCurrentService;
                        });
                      } else {
                        fetchSecondSelect(
                          parsedServices as ScheduleSymptomDetail[],
                          currentService,
                        );
                      }
                    },
                  })}
                  icon={
                    (isLoading || isFetching) && !firstId ? (
                      <SelectLoading />
                    ) : undefined
                  }
                  isDisabled={(isLoading || isFetching) && !firstId}
                >
                  {firstLevelServices?.map((state, index: number) => (
                    <option key={index} value={JSON.stringify(state)}>
                      {state.name}
                    </option>
                  ))}
                </Select>

                {secondLevelServices && secondLevelServices.length > 1 && (
                  <Select
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="grey.600"
                    color="text.100"
                    placeholder="Selecionar detalhes"
                    fontSize="16px"
                    fontWeight="500"
                    {...register('details.1', {
                      onChange: () => {
                        setSecondId(undefined);
                        setThirdId(undefined);
                        setFourthId(undefined);
                        setThirdLevelServices(undefined);
                        setFourthLevelServices(undefined);
                        setValue('details.2', '');
                        setValue('details.3', '');
                        const services = getValues('details');
                        const parsedServices = services?.map(service => {
                          if (service && service !== '')
                            return JSON.parse(service);
                        });
                        if (service) {
                          setCurrentService(prev => {
                            const newCurrentService = {
                              ...prev,
                              details: parsedServices
                                ? [parsedServices[0], parsedServices[1]]
                                : undefined,
                            };

                            fetchThirdSelect(
                              newCurrentService.details as ScheduleSymptomDetail[],
                              newCurrentService,
                            );

                            return newCurrentService;
                          });
                        } else {
                          fetchThirdSelect(
                            parsedServices as ScheduleSymptomDetail[],
                            currentService,
                          );
                        }
                      },
                    })}
                    icon={
                      (isLoading || isFetching) && !secondId ? (
                        <SelectLoading />
                      ) : undefined
                    }
                    isDisabled={(isLoading || isFetching) && !secondId}
                  >
                    {secondLevelServices.map((state, index: number) => (
                      <option key={index} value={JSON.stringify(state)}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}

                {thirdLevelServices && thirdLevelServices.length > 1 && (
                  <Select
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="grey.600"
                    placeholder="Selecionar detalhes"
                    color="text.100"
                    fontSize="16px"
                    fontWeight="500"
                    {...register('details.2', {
                      onChange: () => {
                        setThirdId(undefined);
                        setFourthId(undefined);
                        setFourthLevelServices(undefined);
                        setValue('details.3', '');
                        const services = getValues('details');
                        const parsedServices = services?.map(service => {
                          if (service && service !== '')
                            return JSON.parse(service);
                        });
                        if (service) {
                          setCurrentService(prev => {
                            const newCurrentService = {
                              ...prev,
                              details: parsedServices
                                ? [
                                    parsedServices[0],
                                    parsedServices[1],
                                    parsedServices[2],
                                  ]
                                : undefined,
                            };

                            fetchFourthSelect(
                              newCurrentService.details as ScheduleSymptomDetail[],
                              newCurrentService,
                            );

                            return newCurrentService;
                          });
                        } else {
                          fetchFourthSelect(
                            parsedServices as ScheduleSymptomDetail[],
                            currentService,
                          );
                        }
                      },
                    })}
                    icon={
                      (isLoading || isFetching) && !thirdId ? (
                        <SelectLoading />
                      ) : undefined
                    }
                    isDisabled={(isLoading || isFetching) && !thirdId}
                  >
                    {thirdLevelServices.map((state, index: number) => (
                      <option key={index} value={JSON.stringify(state)}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}

                {fourthLevelServices && fourthLevelServices.length > 1 && (
                  <Select
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="grey.600"
                    placeholder="Selecionar detalhes"
                    color="text.100"
                    fontSize="16px"
                    fontWeight="500"
                    {...register('details.3', {
                      onChange: () => {
                        setFourthId(undefined);
                        const services = getValues('details');
                        const parsedServices = services?.map(service => {
                          if (service && service !== '')
                            return JSON.parse(service);
                        });
                        if (service)
                          setCurrentService(prev => {
                            const newCurrentService = {
                              ...prev,
                              details: parsedServices
                                ? [
                                    parsedServices[0],
                                    parsedServices[1],
                                    parsedServices[2],
                                  ]
                                : undefined,
                            };

                            return newCurrentService;
                          });
                      },
                    })}
                    icon={
                      (isLoading || isFetching) && !fourthId ? (
                        <SelectLoading />
                      ) : undefined
                    }
                    isDisabled={(isLoading || isFetching) && !fourthId}
                  >
                    {fourthLevelServices.map((state, index: number) => (
                      <option key={index} value={JSON.stringify(state)}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}

                <Stack>
                  <Text
                    aria-hidden="true"
                    as="label"
                    fontSize="14px"
                    color="text.300"
                  >
                    Adicione detalhes da solicitação
                  </Text>
                  <Textarea
                    aria-label="Adicione detalhes da solicitação"
                    variant="unstyled"
                    bg="transparent"
                    border="1px solid"
                    borderColor="grey.600"
                    color="text.100"
                    fontSize="16px"
                    fontWeight="500"
                    defaultValue={currentService?.observation}
                    {...register('observation')}
                    p="3"
                    size="lg"
                  />
                </Stack>

                {watch('details.0') && (
                  <Flex
                    mt="8px"
                    w="80%"
                    align="center"
                    justify="space-between"
                    borderColor="text.100"
                    borderStyle="dashed"
                    borderWidth="1px"
                    rounded="md"
                    shadow="sm"
                    role="group"
                    py="15px"
                    px="24px"
                    h="54px"
                    transition="all 150ms ease-in-out"
                    _hover={{
                      shadow: 'md',
                    }}
                    position="relative"
                  >
                    <Text fontSize="12px" color="text.100">
                      Insira um arquivo se necessário
                    </Text>
                    <Input
                      cursor="pointer"
                      type="file"
                      h="100%"
                      width="100%"
                      position="absolute"
                      multiple
                      top="0"
                      left="0"
                      bottom="0"
                      opacity="0"
                      aria-hidden="true"
                      accept="image/png, image/jpg, image/jpeg"
                      border="3px solid red"
                      onChange={e =>
                        handleAddImage(e, service?.id || currentId)
                      }
                    />
                    <Flex bg="grey.200" py="8px" px="10px" rounded="md">
                      <Text fontSize="14px" fontWeight="normal" color="black">
                        Upload
                      </Text>
                    </Flex>
                  </Flex>
                )}

                <Flex align="start" flexDir="column">
                  {filtredImages.map((preview: ScheduleFile, index: number) => (
                    <Flex w="100%" align="center" key={index}>
                      <Box>
                        <Image
                          width="32px"
                          height="32px"
                          rounded="md"
                          src={preview.file}
                        />
                      </Box>
                      <Text
                        noOfLines={1}
                        ml="20px"
                        color="black"
                        wordBreak="break-all"
                      >
                        {preview.name}
                      </Text>
                      <Button
                        bg="transparent"
                        p="0"
                        _hover={{ bg: 'transparent' }}
                        onClick={() => handleDeleteImage(preview.name)}
                      >
                        <Image src={trashSvg} />
                      </Button>
                    </Flex>
                  ))}
                </Flex>

                <Flex justify="end">
                  {!currentService && (
                    <AccordionButton
                      fontWeight={500}
                      borderRadius="4px"
                      border="1px solid"
                      color="blue.0"
                      gap="4px"
                      textDecor="none"
                      onClick={handleSubmit(onSubmit)}
                      justifyContent="center"
                    >
                      Adicionar mais serviços
                    </AccordionButton>
                  )}
                </Flex>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    );
  },
);

Service.displayName = 'Service';

export default Service;
